import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from '@shared/ui/molecules/Dialog';
import { BodyError, BodyLarge, BodyRegular, BodyXXSmall } from '@shared/ui/atoms/fonts/Body';
import { Switch } from '@shared/ui/atoms/Switch';
import TextField from '@shared/ui/atoms/TextField';
import { addNewTeam, resetAddTeamDialog, selectAddTeamProcessingState, } from '@shared/store/addTeamDialog.duck';
import DialogHeader from '@shared/dialogs/DialogHeader';
import { redirectTo, selectCurrentUser } from '@shared/store/auth.duck';
import { Row } from '@shared/ui/atoms/StructuralLayout';
import { LoadingSpinner } from "@shared/ui/atoms/LoadingSpinner";
import { selectAccountSummary } from '@features/settings/store/payments.duck';
import { LinkButton } from "@shared/ui/atoms/buttons/LinkButton";
import { isInTeams } from '@shared/utils/TeamsUtils';
import { PrimaryButton } from "@shared/ui/atoms/buttons/PrimaryButton";
import { BorderlessButton } from "@shared/ui/atoms/buttons/BorderlessButton";
import { useTranslation } from "react-i18next";
import { IconTypes } from "@shared/ui/atoms/icon/Icon";
import { hasUsedTeamAllocation } from '@features/auth/services/AccountService';
import { closeDialog } from '@shared/store/dialog.duck';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export default function AddTeamDialog() {
  const dispatch = useDispatch();
  const close = () => {
    dispatch(closeDialog());
  }

  return (
    <Dialog isOpen={true} onClose={() => close()}>
      <div className="dialog__addMember">
        <AddTeamForm/>
      </div>
    </Dialog>
  )
}

function AddTeamForm() {
  const dialogState = useSelector(selectAddTeamProcessingState);
  const dispatch = useDispatch();
  const [teamName, setTeamName] = useState(dialogState.failedRequest.teamName);
  const [addUserToTeam, setAddUserToTeam] = useState(dialogState.failedRequest.addUserToTeam);
  const currentUser = useSelector(selectCurrentUser);
  const accountSummary = useSelector(selectAccountSummary);
  const dispatchNewTeamRequest = () => dispatch(addNewTeam({teamName, addUserToTeam}));
  const {t} = useTranslation();

  const openAddMemberDialog = () => {
    dispatch(resetAddTeamDialog());
    dispatch(closeDialog());
    dispatch(redirectTo('/manage/add-users'));
  }

  useEffect(() => {
    return () => {
      dispatch(resetAddTeamDialog())
    }
  }, [dispatch]);

  if (dialogState.processing) {
    return (
      <React.Fragment>
        <Row style={{justifyContent: 'center'}}>
          <LoadingSpinner className="dialog__spinner" hideBorder={true}/>
        </Row>
      </React.Fragment>
    )
  }

  if (dialogState.success) {
    return (
      <React.Fragment>
        <DialogHeader title="dialog-headers.team-created" icon={IconTypes.Tick}/>
        <BodyLarge className="addTeamDialog__text">{t('dialog.add-people-to-team-message')}</BodyLarge>
        <PrimaryButton text="button.add-team-members"
                       click={() => openAddMemberDialog()}
                       fullWidth={true}
                       style={{marginBottom: 16}}
                       size={ButtonSize.Large}/>
        <BorderlessButton text="button.add-later"
                          click={() => dispatch(closeDialog())}
                          fullWidth={true}
                          size={ButtonSize.Large}/>
      </React.Fragment>
    )
  }

  if (hasUsedTeamAllocation(accountSummary)) {
    return <React.Fragment>
      <DialogHeader title={isInTeams() ? `dialog.max-teams-header-msteams` : `dialog.max-teams-header`}/>
      <BodyLarge className="addTeamDialog__text">{t(isInTeams() ? `dialog.max-teams-message-msteams` : `dialog.max-teams-message`)}</BodyLarge>
      {isInTeams() ? <PrimaryButton click={() => dispatch(closeDialog())} size={ButtonSize.Large} text="button.ok" /> :
        <LinkButton text="button.upgrade" onClick={() => dispatch(closeDialog())} link="/manage/payments" type="primary" size={ButtonSize.Large}/>}
    </React.Fragment>
  }

  if (!currentUser?.companyEntity) {
    return (
      <React.Fragment>
        <DialogHeader title="dialog.add-new-team"/>
        <BodyRegular>{t('dialog.add-company-to-add-more-teams')}</BodyRegular>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <DialogHeader title="dialog.add-new-team"/>

      <BodyError>{dialogState.error}</BodyError>

      <form onSubmit={(e) => e.preventDefault()}>
        <TextField dataTest="teamDialog-teamName-input"
                   label="textinput.team-name"
                   value={teamName}
                   onChange={(val: string) => setTeamName(val)}/>

        <div className="addTeamDialog__addSelf">
          <Switch label="dialog.add-self-to-team" value={addUserToTeam} switchClass="addTeamDialog__switch"
                  onChange={(val: boolean) => setAddUserToTeam(val)}/>
          <BodyXXSmall>{t('dialog.add-self-to-team-warning')}</BodyXXSmall>
        </div>

        <PrimaryButton text="button.create" click={() => dispatchNewTeamRequest()} buttonType="submit" fullWidth={true}/>
      </form>
    </React.Fragment>
  )
}
